/* eslint max-lines: 0 */
import React, { useState, useEffect } from "react"
import tw, { styled, css, TwStyle } from "twin.macro"
import ReactGA from "react-ga"
import { ReactComponent as SpinnerSVG } from "../../../assets/icons/spinner.svg"

export interface ButtonProps {
  id?: string
  onClick?: (e: React.MouseEvent<Element, MouseEvent>) => void
  onClickAsync?: (e: React.MouseEvent<Element, MouseEvent>) => Promise<void>
  theme?: string
  twStyle?: TwStyle | string
  icon?: JSX.Element
  action?: string
  isProcessing?: boolean
  isDisabled?: boolean
  dataTestId?: string
  tabIndex?: number
  iconBefore?: boolean
}

export enum ButtonTheme {
  PrimaryOutline = "primary-outline",
  PrimaryGhost = "primary-ghost",
  Secondary = "secondary",
  SecondaryOutline = "secondary-outline",
  SecondaryGhost = "secondary-ghost",
  Warning = "warning",
  WarningWhite = "warning-white",
  WarningWhiteOutline = "warning-white-outline",
  WarningOutline = "warning-outline",
  WarningGhost = "warning-ghost",
  Success = "success",
  SuccessOutline = "success-outline",
  SuccessGhost = "success-ghost",
  Twitter = "twitter",
}

export const StyledButton = styled("button")(({ theme }: ButtonProps) => [
  tw`min-w-[76px] py-2 px-6 text-md font-bold rounded-full bg-button-primary text-white`,
  tw`disabled:(cursor-default bg-button-primary-disabled hover:bg-none)`,
  // Add all hover styles within this media query that checks for devices that have hover enabled
  css`
    @media (hover: hover) and (pointer: fine) {
      ${tw`hover:(bg-gradient-to-r from-purple to-dark-purple)`}
    }
  `,

  theme == ButtonTheme.PrimaryOutline && [
    tw`py-1.5 px-6 bg-transparent text-button-primary-outline border-purple border-2
      disabled:(py-2 px-6 bg-light-purple border-none hover:(bg-light-purple bg-clip-padding))
    `,
    css`
      @media (hover: hover) and (pointer: fine) {
        ${tw`hover:(bg-none bg-transparent)`}
        &:hover .action {
          ${tw`text-transparent bg-clip-text bg-gradient-to-r from-purple to-dark-purple`}
          path {
            fill: #7304a7;
          }
        }
        &:disabled .action {
          ${tw`text-white`}
          path {
            fill: #ffffff;
          }
        }
      }
    `,
  ],

  // Twitter button
  theme == ButtonTheme.Twitter && [
    tw`bg-transparent text-blue border-blue border-2`,
    css`
      @media (hover: hover) and (pointer: fine) {
        &:hover,
        &:disabled {
          ${tw`bg-none bg-transparent text-dark-blue border-dark-blue`}
          path {
            fill: #35a4cc;
          }
        }
      }
    `,
  ],

  theme == ButtonTheme.PrimaryGhost && [
    tw`bg-transparent text-purple
      disabled:(bg-none bg-transparent no-underline text-button-primary-disabled)
    `,
    css`
      @media (hover: hover) and (pointer: fine) {
        ${tw`hover:(underline bg-none)`}
        &:hover .action {
          ${tw`text-transparent bg-clip-text bg-gradient-to-r from-purple to-dark-purple`}
        }
        &:disabled .action {
          ${tw`text-button-primary-disabled bg-none bg-transparent`}
        }
      }
    `,
  ],

  theme == ButtonTheme.Secondary && [
    tw`bg-button-secondary text-white
      disabled:(bg-button-secondary-disabled hover:bg-none)
    `,
    css`
      @media (hover: hover) and (pointer: fine) {
        ${tw`hover:(bg-gradient-to-r from-pink to-dark-purple)`}
        &:hover .action {
          ${tw`text-white`}
        }
      }
    `,
  ],

  theme == ButtonTheme.SecondaryOutline && [
    tw`py-1.5 px-6 bg-transparent text-dark-purple border-dark-purple border-2 
      disabled:(py-2 px-6 text-white bg-button-secondary-disabled border-none hover:bg-light-purple bg-clip-padding)
    `,
    css`
      @media (hover: hover) and (pointer: fine) {
        ${tw`hover:(text-transparent bg-clip-text bg-gradient-to-r from-pink to-dark-purple)`}
        &:hover .action {
          ${tw`text-transparent bg-clip-text bg-gradient-to-r from-pink to-dark-purple`}
        }
        &:disabled .action {
          ${tw`text-white bg-none bg-transparent`}
        }
      }
    `,
  ],

  theme == ButtonTheme.SecondaryGhost && [
    tw`bg-transparent text-dark-purple
      disabled:(bg-none bg-transparent no-underline text-button-secondary-disabled)
    `,
    css`
      @media (hover: hover) and (pointer: fine) {
        ${tw`hover:(underline bg-none)`}
        &:hover .action {
          ${tw`text-transparent bg-clip-text bg-gradient-to-r from-pink to-dark-purple`}
        }
        &:disabled .action {
          ${tw`text-button-secondary-disabled bg-none bg-transparent`}
        }
      }
    `,
  ],

  theme == ButtonTheme.Warning && [
    tw`bg-button-warning
      disabled:(bg-button-warning-disabled tablet-lg:hover:bg-button-warning-disabled)
    `,
    css`
      @media (hover: hover) and (pointer: fine) {
        ${tw`hover:(bg-none bg-button-warning) tablet-lg:hover:(bg-none bg-dark-red)`}
      }
    `,
  ],

  theme == ButtonTheme.WarningWhite && [
    tw`bg-white text-red
      disabled:(bg-button-warning-disabled hover:bg-button-warning-disabled)
    `,
    css`
      @media (hover: hover) and (pointer: fine) {
        ${tw`hover:(bg-none bg-dark-red)`}
      }
    `,
  ],

  theme == ButtonTheme.WarningWhiteOutline && [
    tw`py-1.5 px-6 bg-transparent text-button-warning-white border-button-warning-white border-2
      hover:(bg-none border-dark-red text-dark-red) 
      disabled:(py-2 px-6 text-white bg-light-red border-none hover:bg-button-warning-disabled bg-clip-padding)
    `,
    css`
      &:hover .action {
        ${tw`text-dark-red`}
      }
      &:disabled .action {
        ${tw`text-white bg-none bg-transparent`}
      }
    `,
  ],

  theme == ButtonTheme.WarningOutline && [
    tw`py-1.5 px-6 bg-transparent text-button-warning border-button-warning border-2
      disabled:(py-2 px-6 text-white bg-light-red border-none hover:bg-button-warning-disabled bg-clip-padding)
    `,
    css`
      @media (hover: hover) and (pointer: fine) {
        ${tw`hover:(bg-none border-dark-red text-dark-red)`}
        &:hover .action {
          ${tw`text-dark-red`}
        }
        &:disabled .action {
          ${tw`text-white bg-none bg-transparent`}
        }
      }
    `,
  ],

  theme == ButtonTheme.WarningGhost && [
    tw`bg-transparent text-button-warning
      disabled:(bg-none bg-transparent no-underline text-light-red)
    `,
    css`
      @media (hover: hover) and (pointer: fine) {
        ${tw`hover:(underline bg-none)`}
        &:hover .action {
          ${tw`text-button-warning`}
        }
        &:disabled .action {
          ${tw`text-button-warning-disabled bg-none bg-transparent`}
        }
      }
    `,
  ],

  theme == ButtonTheme.Success && [
    tw`bg-button-success
      disabled:(bg-button-success-disabled hover:bg-button-success-disabled)
    `,
    css`
      @media (hover: hover) and (pointer: fine) {
        ${tw`hover:(bg-none bg-dark-green)`}
      }
    `,
  ],

  theme == ButtonTheme.SuccessOutline && [
    tw`py-1.5 px-6 bg-transparent text-button-success border-button-success border-2
      disabled:(py-2 px-6 text-white bg-button-success-disabled border-none hover:bg-button-success-disabled bg-clip-padding)
    `,
    css`
      @media (hover: hover) and (pointer: fine) {
        ${tw`hover:(bg-none border-dark-green text-dark-green)`}
        &:hover .action {
          ${tw`text-dark-green`}
        }
        &:disabled .action {
          ${tw`text-white bg-none bg-transparent`}
        }
      }
    `,
  ],

  theme == ButtonTheme.SuccessGhost && [
    tw`bg-transparent text-button-success
      disabled:(bg-none bg-transparent no-underline text-light-green)
    `,
    css`
      @media (hover: hover) and (pointer: fine) {
        ${tw`hover:(underline bg-none)`}
        &:hover .action {
          ${tw`text-button-success`}
        }
        &:disabled .action {
          ${tw`text-button-success-disabled bg-none bg-transparent`}
        }
      }
    `,
  ],
])

export const Button = ({
  icon,
  iconBefore,
  isProcessing,
  isDisabled,
  id,
  dataTestId,
  action,
  tabIndex,
  theme,
  twStyle,
  onClickAsync,
  onClick,
}: ButtonProps) => {
  const [awaiting, setAwaiting] = useState(false)

  const handleClick = async (e: React.MouseEvent<Element, MouseEvent>) => {
    if (process.env.NODE_ENV === "production") {
      ReactGA.event({
        category: "User",
        action: action ?? "clicked a button",
      })
    }
    // pass in onClickAsync instead of onClick if we are awaiting for a promise to resolve.
    if (onClickAsync) {
      setAwaiting(true)
      await onClickAsync(e)
      setTimeout(() => setAwaiting(false), 2000)
    } else {
      onClick?.(e)
    }
  }
  useEffect(() => {
    return () => setAwaiting(false) // canceling async task fixes react warning can't update an unmounted component
  }, [])

  return (
    <StyledButton
      id={id}
      type="submit"
      disabled={isProcessing || isDisabled || awaiting}
      onClick={async (e: React.MouseEvent<Element, MouseEvent>) => await handleClick(e)}
      data-testid={dataTestId}
      title={action}
      tabIndex={tabIndex}
      theme={theme}
      icon={icon}
      css={twStyle}
    >
      <span className="action" tw="flex justify-center items-center gap-x-2 whitespace-nowrap relative">
        {iconBefore && <span>{icon}</span>}
        {action}
        {isProcessing || awaiting ? <SpinnerSVG tw="-mr-2 w-4 h-4 animate-spin" /> : !iconBefore && icon}
      </span>
    </StyledButton>
  )
}

Button.displayName = "Button" // displayName is the name that React uses in debugging this is here to please the linter
